import * as Styled from 'components/SearchPage/components/Listings/components/SearchCard/SearchCard.styled';

import { PlayOutlineIc, RepeatOutlineIc } from '@dsch/dd-icons';

import type { SearchCardProps } from 'components/SearchPage/components/Listings/components/SearchCard/SearchCard.typed';

import { Price } from 'components/ToolkitV2/CardV2/components/Price/Price';
import { Image } from 'components/ToolkitV2/CardV2/components/Image/Image';
import { Tags } from 'components/SearchPage/components/Listings/components/Tags';
import { Card } from 'components/ToolkitV2/CardV2/Card';
import { BasicHeader } from 'components/ToolkitV2/CardV2/components/BasicHeader/BasicHeader';
import { TrustedDealerBadge } from 'components/PageWrappers/SharedComponents/TrustedDealerBadge/TrustedDealerBadge';
import { InfoTitleStarRating } from 'components/PageWrappers/AdDetails/components/GoogleReviews/InfoTitleStarRating/InfoTitleStarRating';
import { MediaCount } from 'components/Toolkit/Tags/MediaCount';
import { MediaHighlight } from 'components/Toolkit/Tags/MediaHighlight';
import { Spacer } from 'helpers/genericStyles';

import { useSearchCard } from 'components/SearchPage/components/Listings/components/SearchCard/SearchCard.hook';
import { useSaveAd } from 'components/Support/UseSaveAd/useSaveAd';
import { useCampaignURLHandler } from 'components/PageWrappers/Hooks/Campaigns/UseCampaignURLHandler';

import { WithLink } from 'components/ToolkitV2/CardV2/enhancements/WithLink/WithLink';
import { WithHighlight } from 'components/ToolkitV2/CardV2/enhancements/WithHighlight/WithHighlight';
import { WithImageOverlay } from 'components/ToolkitV2/CardV2/enhancements/WithImageOverlay/WithImageOverlay';
import { WithHeader } from 'components/ToolkitV2/CardV2/enhancements/WithHeader/WithHeader';
import { ORIENTATION } from 'components/ToolkitV2/CardV2/Card.typed';

import { Desktop } from 'components/Support/Desktop/Desktop';

import { useUserContext } from 'contexts/UserContext';
import { useAuthOptions } from 'hooks/useAuthOptions';
import { formatAuthorizationHeader } from 'helpers/auth';
import { PAGE } from 'helpers/pages';
import { renderTrustedDealerInSection } from 'helpers/trustedDealerProgram';

function SearchCard(props: SearchCardProps) {
  const {
    item: {
      dealer,
      section,
      id,
      userSaved,
      age,
      county,
      countyTown,
      wanted,
      deliveryAvailable,
      cardPayments,
      meritsV2,
      specialOffer,
      greenlightVerified,
      manufacturerApprovedBadge,
      herdwatchVerified,
      friendlyUrl,
      highlight,
      highlightText,
      photos,
      imageAlt,
      mediaCount,
      header,
      currency,
      price,
      priceOnRequest,
      financeSummary,
      keyInfo,
      isJato,
      jatoPayload,
      youTubeVideoId,
      link360,
      link360Interior,
      oldPriceView,
    },
    isGrid,
    parentSection,
    createSavedAdEvent,
    deleteSavedAdEvent,
  } = props;

  const displayGoogleReviews = Boolean(
    dealer?.googleReview &&
      dealer?.trustedDealer &&
      dealer?.franchiseType &&
      dealer?.name,
  );

  const displayTrustedDealer =
    dealer?.trustedDealer && renderTrustedDealerInSection(section.name);

  const dealerHeader = {
    title: dealer?.name,
    subTitle: displayGoogleReviews ? (
      <InfoTitleStarRating
        rating={dealer?.googleReview?.rating}
        maxRating={5}
        isDealer={true}
        franchiseType={dealer?.franchiseType}
        isTrader={false}
        variant="SMALL"
        styleAsLink={false}
        isFakeLink={true}
      />
    ) : (
      dealer?.franchiseType
    ),
    logo: dealer?.logo?.small,
  };

  const orientationConfig = {
    orientationSmall: ORIENTATION.VERTICAL,
    orientationMedium: isGrid ? ORIENTATION.VERTICAL : ORIENTATION.HORIZONTAL,
    orientationLarge: isGrid ? ORIENTATION.VERTICAL : ORIENTATION.HORIZONTAL,
  };

  const includesPhoto = photos && (photos[0].small2Webp || photos[0].small2);

  const { injectedKeyInfo } = useSearchCard({
    parentSection,
    section,
    isGrid,
    age,
    county,
    countyTown,
    keyInfo,
  });

  const { containsCampaign, handleCampaignAdRouterPush } =
    useCampaignURLHandler();

  const isCampaignLink = containsCampaign(friendlyUrl);

  const { user, isLoginModalOpen } = useUserContext();
  const { accessToken, handleLogin } = useAuthOptions();
  const authorisationHeader = formatAuthorizationHeader(accessToken);

  const { onSave, isAdSaved } = useSaveAd({
    adId: id,
    userSaved: userSaved,
    section: section.name,
    sellerType: dealer ? 'DEALER' : 'PRIVATE',
    sectionName: section.name,
    userId: user?.id,
    isLoginModalOpen,
    isJato,
    jatoPayload,
    trackCreateSaveAd: createSavedAdEvent,
    trackDeleteSaveAd: deleteSavedAdEvent,
    authorisationHeader,
    handleLogin,
  });

  return (
    <WithLink
      href={friendlyUrl}
      isFakeLink={isCampaignLink}
      handleClick={() => handleCampaignAdRouterPush(friendlyUrl)}
    >
      <Styled.CardWrapper>
        <WithHeader
          isGrid={isGrid}
          header={dealerHeader}
          includesHighlight={Boolean(highlight && highlightText)}
          rightSlot={
            displayTrustedDealer && (
              <TrustedDealerBadge
                pageName={PAGE.SEARCH}
                icon={
                  <Styled.SShieldCheckmark2Ic
                    width="24"
                    height="24"
                    data-testid="shield-icon"
                  />
                }
              />
            )
          }
        >
          <WithHighlight
            highlight={highlight}
            highlightText={highlightText}
            isGrid={isGrid}
          >
            <Card
              {...orientationConfig}
              image={
                <WithImageOverlay
                  bottomLeftSlot={
                    <>
                      {includesPhoto && <MediaCount mediaCount={mediaCount} />}
                      {youTubeVideoId && (
                        <MediaHighlight>
                          <PlayOutlineIc width="16" height="16" />
                        </MediaHighlight>
                      )}
                      {(link360 || link360Interior) && (
                        <MediaHighlight>
                          <RepeatOutlineIc height="16" width="16" />
                        </MediaHighlight>
                      )}
                    </>
                  }
                  bottomRightSlot={
                    isGrid ? (
                      <Styled.ImageSaveAd
                        toggleSave={onSave}
                        isAdSaved={isAdSaved}
                        hideText
                        variant="OVERLAY"
                      />
                    ) : undefined
                  }
                >
                  <Image
                    src={
                      photos && photos[0].small2 ? photos[0].small2 : undefined
                    }
                    imgSrcWebp={
                      photos && photos[0].small2Webp
                        ? photos[0].small2Webp
                        : undefined
                    }
                    alt={imageAlt}
                    widthVertical={300}
                    heightVertical={225}
                    widthHorizontalMedium={320}
                    {...orientationConfig}
                  />
                </WithImageOverlay>
              }
              header={
                <div>
                  {!isGrid && (
                    <Desktop>
                      <Spacer marginBottom="S8" />
                    </Desktop>
                  )}
                  <BasicHeader
                    title={header}
                    fontSizeSmall={isGrid ? 'B12' : 'L18'}
                    metaData={injectedKeyInfo}
                    isGrid={isGrid}
                  />
                </div>
              }
              body={
                !isGrid && (
                  <Tags
                    deliveryAvailable={deliveryAvailable}
                    cardPayments={cardPayments}
                    meritsV2={meritsV2}
                    specialOffer={specialOffer}
                    wanted={wanted}
                    greenlightVerified={greenlightVerified}
                    manufacturerApprovedBadge={manufacturerApprovedBadge}
                    herdwatchVerified={herdwatchVerified}
                  />
                )
              }
              gapSmall={isGrid ? 'M16' : 'S8'}
              gapMedium={isGrid ? 'S8' : undefined}
              paddingSmall={isGrid ? 'S4' : 'S8'}
              paddingMedium={isGrid ? 'S8' : 'M16'}
              footer={
                <>
                  <Price
                    currency={currency}
                    price={price}
                    pricePerMonth={financeSummary?.price}
                    priceOnRequest={priceOnRequest}
                    isGrid={isGrid}
                    oldPrice={
                      oldPriceView.showOldPrice ? oldPriceView.oldPrice : null
                    }
                  />
                  <Styled.BodySaveAd
                    toggleSave={onSave}
                    isAdSaved={isAdSaved}
                    hideText
                    isGrid={isGrid}
                  />
                </>
              }
            />
          </WithHighlight>
        </WithHeader>
      </Styled.CardWrapper>
    </WithLink>
  );
}

export { SearchCard };
